import { QueryClient } from "@tanstack/solid-query";
import { createSignal } from "solid-js";
import { Button } from "~/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "~/components/ui/dialog";
import { useTopAlert } from "~/components/ui/topAlert";
import { GoogleOAuth2Scopes, googleOAuth2 } from "~/lib/oauth2";
import {
  channelControllerAddYoutubeChannelApiV1,
  getChannelControllerAddYoutubeChannelApiV1Url,
} from "../api/__generated__/api.schema";
import type { AddYoutubeChannelResponseDto } from "../api/__generated__/models";
import { MixpanelEvent, trackEvent } from "../mixpanel";

export const useAddChannel = () => {
  const [showAlreadyOwnedDialog, setShowAlreadyOwnedDialog] = createSignal(false);
  const topAlert = useTopAlert();

  return [
    async () => {
      const result = await googleOAuth2(GoogleOAuth2Scopes.YouTube);
      // TODO: Use useQueryClient instead. For now, it's not working.
      const queryClient = new QueryClient();
      const query = await queryClient.fetchQuery({
        queryKey: [getChannelControllerAddYoutubeChannelApiV1Url()],
        queryFn: () =>
          channelControllerAddYoutubeChannelApiV1({
            youtubeAccessToken: result.access_token,
          }),
      });
      if (query.status === 201) {
        const channel = (query.data as AddYoutubeChannelResponseDto).data;
        trackEvent(MixpanelEvent.ChannelAdded, {
          channel_id: channel.id,
          is_success: true,
        });
      } else if (query.status === 404) {
        topAlert.open({
          variant: "error",
          children: (
            <div>
              <p>Looks like the Google account you are using</p>
              <p>doesn't have any YouTube channel 🥲</p>
            </div>
          ),
        });
      } else if (query.status === 409) {
        setShowAlreadyOwnedDialog(true);
      } else if (query.status === 422) {
        topAlert.open({
          variant: "error",
          duration: 10000,
          children: (
            <div>
              <p>Failed to determine channel to fetch</p>
              <p>
                Please contact us through <a href="mailto:support@backpac.app">support@backpac.app</a>
              </p>
            </div>
          ),
        });
      } else {
        topAlert.open({
          variant: "error",
          children: (
            <div>
              <p>Failed to fetch YouTube channel</p>
              <p>Please try again</p>
            </div>
          ),
        });
      }
    },
    () => (
      <Dialog open={showAlreadyOwnedDialog()} onOpenChange={setShowAlreadyOwnedDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Check your account</DialogTitle>
          </DialogHeader>
          <p class="env-mobile:prose-sm">
            The YouTube channel you are trying to add is already linked to another BackPac account. Each channel can
            only connect to one account. Please check your account or use a different channel.
          </p>
          <DialogFooter>
            <Button onClick={() => setShowAlreadyOwnedDialog(false)}>Okay</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    ),
  ] as const;
};
